import "./instrument"; // Must be the very first import — initializes Sentry.
import { Capacitor } from "@capacitor/core";
import { captureException } from "@sentry/react";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ThemeProvider } from "next-themes";
import React, { Component, type ErrorInfo, type ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";

import "./index.css";
import mixpanel from "@/lib/mixpanel";

import { SupabaseProvider } from "./api/SupabaseProvider";
import App from "./App";
import { SomethingWentWrong } from "./components/ui/errors";
import { ReflagFlagsProvider } from "./config/reflag";
import { AvatarGradientProvider } from "./contexts/AvatarGradientContext";
import { initKeyboardHandler } from "./hooks/useKeyboard";
import { registerSuperProperties } from "./lib/analyticsIdentity";
import { triggerReloadOnce } from "./lib/lazyWithRetry";
import { queryStorage, STORAGE_KEY } from "./lib/query-storage";
import { initSessionTracking } from "./lib/sessionAnalytics";

mixpanel.init("e48a249668047adb55dddf95d4754e03", {
  autocapture: true,
  record_sessions_percent: 0,
  record_allowed_iframe_origins: [
    "http://localhost:5174",
    "https://app.mashit.dev",
  ],
  record_heatmap_data: true,
  record_console: true,
  record_network: true,
  save_referrer: true,
  store_google: true,
  track_pageview: true,
});

mixpanel.set_config({ ignore_dnt: true });

// Also re-applied after `mixpanel.reset()` on sign-out, which clears them.
registerSuperProperties();

// Fires `session_started` for this boot and watches foreground/background so
// a long-lived native app reports one session per real visit, not per install.
initSessionTracking();

// Initialize keyboard handler for modal/drawer keyboard avoidance
initKeyboardHandler();

// Set status bar style: Style.Light = dark icons on light background (both platforms)
// Applied in capacitor.config.ts at init, re-applied here as safety net
if (Capacitor.isNativePlatform()) {
  import("@capacitor/status-bar")
    .then(({ StatusBar, Style }) => {
      StatusBar.setStyle({ style: Style.Light });
      // Re-apply after a delay in case overlaysWebView resets the style
      setTimeout(() => StatusBar.setStyle({ style: Style.Light }), 500);
    })
    .catch(() => {});
}

// Suppress ResizeObserver loop error (common with Monaco Editor, completely harmless)
const resizeObserverErr = window.onerror;
window.onerror = (message, ...args) => {
  if (typeof message === "string" && message.includes("ResizeObserver loop")) {
    return true;
  }
  return resizeObserverErr ? resizeObserverErr(message, ...args) : false;
};

console.log("[main.tsx] All imports loaded successfully");

// Suppress harmless errors globally
window.addEventListener("error", event => {
  const message = event.message || "";
  // React 18 StrictMode + Radix portal errors
  if (message.includes("removeChild") && message.includes("not a child")) {
    event.preventDefault();
    console.warn("Suppressed harmless portal error:", message);
    return true;
  }
  // ResizeObserver loop errors (common with Monaco Editor)
  if (message.includes("ResizeObserver loop")) {
    event.preventDefault();
    return true;
  }
});

// Backstop for stale-chunk failures: Vite emits `vite:preloadError` when a
// dependency chunk fails to preload after a deploy re-hashed it. Reload once to
// pick up the fresh build (shares the guard in lazyWithRetry so the route
// import() path and this path can't double-reload). See lib/lazyWithRetry.ts.
window.addEventListener("vite:preloadError", () => {
  triggerReloadOnce();
});

// Error Boundary to catch and display errors
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Known harmless errors to ignore (React 18 StrictMode + Radix UI portals)
const isHarmlessError = (error: Error): boolean => {
  const message = error.message || "";
  // React 18 StrictMode double-render causes portal cleanup issues
  if (message.includes("removeChild") && message.includes("not a child")) {
    return true;
  }
  return false;
};

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Ignore harmless errors from React 18 StrictMode + Radix portals
    if (isHarmlessError(error)) {
      console.warn("Ignoring harmless portal error:", error.message);
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!isHarmlessError(error)) {
      console.error("Error caught by boundary:", error, errorInfo);
      captureException(error, {
        contexts: {
          react: { componentStack: errorInfo.componentStack ?? undefined },
        },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return <SomethingWentWrong fullPage />;
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-while-revalidate: render the persisted/cached value
      // immediately, then always kick off a background refetch so the UI
      // converges on fresh server data.
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours — keep in cache for persistence
      retry: 1,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: queryStorage,
  key: STORAGE_KEY,
});

function mount() {
  // No storage init needed: `queryStorage` reads localStorage synchronously, so
  // PersistQueryClientProvider hydrates on its first getItem.
  console.log("[main.tsx] About to mount React...");
  const rootElement = document.getElementById("root");
  console.log("[main.tsx] Root element:", rootElement);

  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <ErrorBoundary>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: 1000 * 60 * 60 * 24, // 24 hours
              buster: "2026-05-05-no-reorder", // change to bust cache on deploy
            }}
          >
            <ThemeProvider
              attribute="class"
              defaultTheme="light"
              storageKey="tinyapps-theme"
            >
              <SupabaseProvider>
                <AvatarGradientProvider>
                  {/* Inside SupabaseProvider so flag evaluation sees the
                      restored auth session, outside the router so a route
                      change never remounts the Reflag client. */}
                  <ReflagFlagsProvider>
                    <BrowserRouter>
                      <App />
                    </BrowserRouter>
                  </ReflagFlagsProvider>
                </AvatarGradientProvider>
              </SupabaseProvider>
            </ThemeProvider>
          </PersistQueryClientProvider>
        </ErrorBoundary>
      </React.StrictMode>,
    );
    console.log("[main.tsx] React mounted");
  } else {
    console.error("[main.tsx] Root element not found!");
  }
}

mount();
